import { z } from "zod"

import type { StoreConfig } from "./types"

/** The storefront's legal pages, each with its own route (`/<kind path>`). */
export const LEGAL_DOCUMENT_KINDS = [
  "allergyAdvice",
  "termsAndConditions",
  "privacyPolicy",
  "cookiePolicy",
] as const

export type LegalDocumentKind = (typeof LEGAL_DOCUMENT_KINDS)[number]

/** A legal document with its fields validated; `null`s for what's absent. */
export interface ResolvedLegalDocument {
  text: string | null
  url: string | null
  updatedAt: string | null
}

/** `StoreConfig.legal` with every field validated. */
export interface ResolvedLegalSettings {
  legalName: string | null
  companyNumber: string | null
  vatNumber: string | null
  registeredAddress: string | null
  /** `null` ⇒ the tenant supplied neither text nor a link for it. */
  documents: Record<LegalDocumentKind, ResolvedLegalDocument | null>
}

/** Long enough for a full privacy notice or set of terms. */
export const LEGAL_TEXT_MAX_LENGTH = 100_000

const shortText = (max: number) =>
  z.string().trim().min(1).max(max).optional().catch(undefined)

const legalDocumentSchema = z
  .object({
    text: z
      .string()
      .trim()
      .min(1)
      .max(LEGAL_TEXT_MAX_LENGTH)
      .optional()
      .catch(undefined),
    url: z
      .url({ protocol: /^https$/ })
      .max(2048)
      .optional()
      .catch(undefined),
    updatedAt: z.iso.date().optional().catch(undefined),
  })
  .catch({})

const legalSettingsSchema = z
  .object({
    legalName: shortText(160),
    companyNumber: shortText(32),
    vatNumber: shortText(32),
    registeredAddress: shortText(300),
    privacyPolicy: legalDocumentSchema.optional().catch(undefined),
    termsAndConditions: legalDocumentSchema.optional().catch(undefined),
    allergyAdvice: legalDocumentSchema.optional().catch(undefined),
    cookiePolicy: legalDocumentSchema.optional().catch(undefined),
  })
  .partial()
  .catch({})

/**
 * Narrow a tenant's raw `legal` settings. Each field is validated on its own
 * and a bad one is dropped without costing the rest: over-long text, a
 * non-`https:` link (no `javascript:` or plain-http links in the footer), a
 * malformed date. A document with neither text nor link resolves to `null`.
 */
export function resolveLegalSettings(
  config: Pick<StoreConfig, "legal">
): ResolvedLegalSettings {
  const raw = legalSettingsSchema.parse(config.legal ?? {})
  const documents = {} as ResolvedLegalSettings["documents"]
  for (const kind of LEGAL_DOCUMENT_KINDS) {
    const doc = raw[kind]
    documents[kind] =
      doc && (doc.text || doc.url)
        ? {
            text: doc.text ?? null,
            url: doc.url ?? null,
            updatedAt: doc.updatedAt ?? null,
          }
        : null
  }
  return {
    legalName: raw.legalName ?? null,
    companyNumber: raw.companyNumber ?? null,
    vatNumber: raw.vatNumber ?? null,
    registeredAddress: raw.registeredAddress ?? null,
    documents,
  }
}

/** One block of a legal document's text, ready to render as elements. */
export type LegalTextBlock =
  | { type: "heading"; level: 1 | 2; text: string }
  | { type: "paragraph"; lines: string[] }
  | { type: "list"; items: string[] }

/**
 * Parse a legal document's plain text (`LegalDocument.text`) into blocks —
 * never HTML, so tenant text can't inject markup:
 *   - a blank line ends a block;
 *   - `# Title` / `## Section` lines are headings;
 *   - consecutive `- item` (or `* item`) lines form a bullet list;
 *   - other consecutive lines form a paragraph (kept as separate lines).
 */
export function parseLegalText(text: string): LegalTextBlock[] {
  const blocks: LegalTextBlock[] = []
  let paragraph: string[] | null = null
  let list: string[] | null = null
  const flush = () => {
    if (paragraph) blocks.push({ type: "paragraph", lines: paragraph })
    if (list) blocks.push({ type: "list", items: list })
    paragraph = null
    list = null
  }
  for (const rawLine of text.replace(/\r\n?/g, "\n").split("\n")) {
    const line = rawLine.trim()
    if (!line) {
      flush()
      continue
    }
    const heading = /^(#{1,2})\s+(.+)$/.exec(line)
    if (heading) {
      flush()
      blocks.push({
        type: "heading",
        level: heading[1]!.length as 1 | 2,
        text: heading[2]!.trim(),
      })
      continue
    }
    const item = /^[-*]\s+(.+)$/.exec(line)
    if (item) {
      if (paragraph) flush()
      ;(list ??= []).push(item[1]!.trim())
      continue
    }
    if (list) flush()
    ;(paragraph ??= []).push(line)
  }
  flush()
  return blocks
}
